import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useProjectViewStore } from '@/stores/projectView';
import { usePermissionsStore } from '@/stores/permissions';
import type { ProjectDetail } from '@/types/odoo';
import { fetchProjectFases, createProjectFase, renameProjectFase, setCollectorenGeleverd, setFaseInterventie, type DashboardFase } from '@/api/projectFases';
import { faseAggregate, faseDateRange, faseStatus, groupCollectorsByFase, nearestFutureFase } from '@/utils/dashboardFases';
import { dateKey, faseStartWeek, planningTasksForFase, unplannedTasksForFase } from '@/utils/dashboardPlanning';
import { buildFaseGroups } from '@/utils/materiaal';
import {
  activiteitSummary,
  collectorenSummary,
  countFotosWachten,
  materiaalSummary,
  planningSummary,
  vorderingSummary,
  type VorderingSummaryData,
} from '@/utils/dashboardSummaries';
import { PartnerWarningBanner } from '@/components/ui/PartnerWarningBanner';
import { DashboardHeaderCard } from './DashboardHeaderCard';
import { DashboardNotesSection } from './DashboardNotesSection';
import { DashboardSection } from './DashboardSection';
import { DashboardStatusLine } from './DashboardStatusLine';
import { FaseTabBar } from './FaseTabBar';
import { FaseModal } from './FaseModal';
import { FaseStatusStrip } from './FaseStatusStrip';
import { InplannenPanel } from './InplannenPanel';
import { DashboardCollectorenSection } from './DashboardCollectorenSection';
import { DashboardCollectorenGeleverdBox } from './DashboardCollectorenGeleverdBox';
import { DashboardMateriaalSection, buildActiveFaseGroup } from './DashboardMateriaalSection';
import { DashboardMateriaalAlles } from './DashboardMateriaalAlles';
import { DashboardActivityWidget } from './DashboardActivityWidget';
import { DashboardVorderingSection } from './DashboardVorderingSection';
import { DashboardInterventieBlocks } from './DashboardInterventieBlocks';
import { DashboardOfferteSection } from './DashboardOfferteSection';
import { InstallateurTaakContent } from './InstallateurTaakContent';
import { DEFAULT_OPEN, FASE_INDEPENDENT_SECTIONS, type SectionKey } from './dashboardSections';

interface DashboardTabProps {
  detail: ProjectDetail;
  /**
   * One-shot focus request from the phased-out Collectoren tab redirect: when set
   * to a section key (e.g. 'collectoren'), that section is expanded and scrolled
   * into view once the dashboard has loaded. Cleared via onFocusConsumed.
   */
  focusSection?: string | null;
  onFocusConsumed?: () => void;
}

/**
 * Project Dashboard — single long-scroll overview that will replace the
 * multi-tab ProjectView for projectleiders (see plans/dashboard-revamp.md).
 *
 * Admin-only via permissions.tabs until feature-complete. Sections land
 * phase-by-phase: Fase 1 = header card; Fase 2 = fase tabs + status strip;
 * Fase 3a = read-only planning grid + inline task detail.
 */
export function DashboardTab({ detail, focusSection, onFocusConsumed }: DashboardTabProps) {
  const { collectors, collectorsLoading, collectorsError, collectorProjectId, fetchCollectoren } =
    useProjectViewStore(
      useShallow((s) => ({
        collectors: s.collectors,
        collectorsLoading: s.collectorLoading,
        collectorsError: s.collectorError,
        collectorProjectId: s.collectorProjectId,
        fetchCollectoren: s.fetchCollectoren,
      })),
    );

  const { materiaalProjectId, materiaalPickings, materiaalLines, materiaalMoves, materiaalLoading, materiaalError, fetchMateriaallijst } =
    useProjectViewStore(
      useShallow((s) => ({
        materiaalProjectId: s.materiaalProjectId,
        materiaalPickings: s.materiaalPickings,
        materiaalLines: s.materiaalLines,
        materiaalMoves: s.materiaalMoves,
        materiaalLoading: s.materiaalLoading,
        materiaalError: s.materiaalError,
        fetchMateriaallijst: s.fetchMateriaallijst,
      })),
    );

  const refreshProject = useProjectViewStore((s) => s.refreshProject);

  // Server-derived dashboard capabilities: gate each section's render (and thus its
  // mount-time fetch/write) on the matching flag so the UI never fires a call the
  // server would 403. Fail-closed until perms load. (Photo VIEW is gated inside
  // DashboardCollectorenSection itself.)
  const dashboardCap = usePermissionsStore((s) => s.dashboardCap);
  const canNotesWrite = dashboardCap('notesWrite');
  const canPlanningWrite = dashboardCap('planningWrite');
  const canPhotosRead = dashboardCap('photosRead');
  const canVorderingRead = dashboardCap('vorderingRead');
  const canActivityRead = dashboardCap('activityRead');
  const canChatterRead = dashboardCap('chatterRead');
  const canOfferteRead = dashboardCap('offerteRead');
  const canInstallateurTaak = usePermissionsStore((s) => s.canTab)('installateur_taak'); // = the old tab's rule

  // Collectors are a lazy store slice (not part of `detail`); load them for the
  // header legplan fallback + the per-fase aggregates.
  useEffect(() => {
    if (collectorProjectId !== detail.projectId) {
      void fetchCollectoren(detail.projectId);
    }
  }, [detail.projectId, collectorProjectId, fetchCollectoren]);

  // Materiaallijsten are a lazy store slice too — load for the materiaal section
  // (Fase 6b), guarded against a previously-opened project (mirror the collectors guard).
  useEffect(() => {
    if (materiaalProjectId !== detail.projectId) {
      void fetchMateriaallijst(detail.projectId);
    }
  }, [detail.projectId, materiaalProjectId, fetchMateriaallijst]);

  // Guard against stale collectors from a previously-opened project.
  const collectorsForProject = collectorProjectId === detail.projectId ? collectors : null;
  // Stale-guard for materiaal: only trust the store slices once they're for this project.
  const materiaalForProject = materiaalProjectId === detail.projectId;

  // Latest project id — lets async callbacks drop a stale response after a project
  // switch (DashboardTab is NOT remounted per project, so closures can outlive it).
  const projectIdRef = useRef(detail.projectId);
  useEffect(() => {
    projectIdRef.current = detail.projectId;
  }, [detail.projectId]);

  // Fases + collector→fase mapping (resolved server-side). Dashboard-local state.
  const [fases, setFases] = useState<DashboardFase[]>([]);
  const [collectorFases, setCollectorFases] = useState<Record<number, number>>({});
  // Werf-level "collectoren al geleverd" flag (fase-level boolean, aggregated).
  const [collectorenGeleverd, setCollectorenGeleverdState] = useState(false);
  const [collectorenGeleverdSaving, setCollectorenGeleverdSaving] = useState(false);
  const [fasesLoading, setFasesLoading] = useState(true);
  const [fasesError, setFasesError] = useState<string | null>(null);
  const [activeFase, setActiveFase] = useState<number | 'alles' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFasesLoading(true);
    setFasesError(null);
    // Clear prior project's fases too — ProjectView does not remount DashboardTab
    // on a projectId change, so without this the old tabs/strip flash during the
    // in-flight fetch.
    setFases([]);
    setCollectorFases({});
    setCollectorenGeleverdState(false);
    setActiveFase(null);
    fetchProjectFases(detail.projectId)
      .then((res) => {
        if (cancelled) return;
        setFases(res.fases);
        setCollectorFases(res.collectorFases);
        setCollectorenGeleverdState(res.collectorenGeleverd);
        // Default to the "Alles" overview when there's more than one fase (its tab
        // is gated to ≥2 fases); a single-fase project opens straight on that fase.
        setActiveFase(res.fases.length >= 2 ? 'alles' : nearestFutureFase(res.fases));
        setFasesLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFasesError(err instanceof Error ? err.message : 'Fases ophalen mislukt');
        setFasesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detail.projectId]);

  // Deep-link: `?collector=<id>` (e.g. from the onderaannemer PO views) selects that
  // collector's fase once the mapping loads. Consumed once per param value so it never
  // fights a later manual fase click; ignored if the collector isn't in this project.
  const [searchParams] = useSearchParams();
  const collectorParam = searchParams.get('collector');
  const consumedCollectorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!collectorParam || consumedCollectorRef.current === collectorParam) return;
    const cid = Number(collectorParam);
    if (!Number.isFinite(cid)) return;
    const faseId = collectorFases[cid];
    if (faseId == null) return; // mapping not loaded yet, or collector not in this project
    consumedCollectorRef.current = collectorParam;
    setActiveFase(faseId);
  }, [collectorParam, collectorFases]);

  // Re-fetch the collector→fase mapping after a collector mutation (create/edit/
  // delete). fetchCollectoren refreshes store.collectors, but the mapping is
  // dashboard-local state fetched once on mount — without this a newly-created
  // collector is dropped from grouping (invisible) until a full reload. Preserves
  // the active fase (only re-selects if it vanished).
  const refetchFaseMapping = useCallback(() => {
    const pid = detail.projectId;
    fetchProjectFases(pid)
      .then((res) => {
        // Drop a stale response if the user switched project mid-refetch (no remount,
        // so this closure can outlive the project it was fired for).
        if (projectIdRef.current !== pid) return;
        setFases(res.fases);
        setCollectorFases(res.collectorFases);
        setCollectorenGeleverdState(res.collectorenGeleverd);
        setActiveFase((prev) =>
          prev === 'alles' || (typeof prev === 'number' && res.fases.some((f) => f.id === prev))
            ? prev
            : nearestFutureFase(res.fases),
        );
      })
      .catch(() => {
        /* keep the current mapping on a transient error — store.collectors still refreshed */
      });
  }, [detail.projectId]);

  // Bumped on every collector mutation from the collectoren section (create / edit /
  // delete / photo upload). The vordering section fetches its OWN data (all_collectors
  // → op-druk/vordered flags that decide which rows are selectable) and would otherwise
  // stay stale: a collector just flipped to «op druk» / uitgevoerd wouldn't appear as
  // selectable-to-vorderen until a full page reload. Feeding this as a signal reloads it.
  const [collectorRev, setCollectorRev] = useState(0);
  const handleCollectorChanged = useCallback(() => {
    setCollectorRev((r) => r + 1);
    refetchFaseMapping();
  }, [refetchFaseMapping]);

  // Werf-level "collectoren geleverd" toggle. Optimistic flip, reverted on error.
  const handleToggleCollectorenGeleverd = useCallback(
    async (next: boolean) => {
      const pid = detail.projectId;
      setCollectorenGeleverdSaving(true);
      setCollectorenGeleverdState(next); // optimistic
      try {
        const res = await setCollectorenGeleverd(pid, next);
        if (projectIdRef.current === pid) setCollectorenGeleverdState(res.collectorenGeleverd);
      } catch {
        if (projectIdRef.current === pid) setCollectorenGeleverdState(!next); // revert
      } finally {
        if (projectIdRef.current === pid) setCollectorenGeleverdSaving(false);
      }
    },
    [detail.projectId],
  );

  // Bumped when interventie photos are uploaded/deleted in DashboardInterventieSection.
  // Fed to the vordering section so its interventie date-groups reload — but NOT via
  // handleCollectorChanged, which also refetches the fase mapping (pointless here:
  // interventie has no fase).
  const [interventieRev, setInterventieRev] = useState(0);
  const handleInterventieChanged = useCallback(() => {
    setInterventieRev((r) => r + 1);
  }, []);

  // Distinct project planning days (every non-collector task that has a date),
  // labelled by fase. These are the ONLY days a PL can re-date interventie photos
  // onto — they must land on a real planned work day so they bill per fase.
  const interventiePlanningDates = useMemo(() => {
    const byDate = new Map<string, Set<string>>();
    for (const t of detail.projectTasks) {
      if (t.x_studio_collector) continue;
      if (!t.date_deadline) continue;
      const day = String(t.date_deadline).slice(0, 10);
      const parentId = Array.isArray(t.parent_id) ? t.parent_id[0] : null;
      const fase = parentId != null ? fases.find((f) => f.id === parentId) : null;
      const label = fase?.name ?? (Array.isArray(t.parent_id) ? t.parent_id[1] : t.name);
      if (!byDate.has(day)) byDate.set(day, new Set());
      if (label) byDate.get(day)!.add(String(label));
    }
    return [...byDate.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, names]) => ({ date, label: [...names].join(', ') }));
  }, [detail.projectTasks, fases]);

  // "Nieuwe fase toevoegen" dialog state + the next "Fase N" suggestion.
  const [addFaseOpen, setAddFaseOpen] = useState(false);
  // The fase whose rename/delete dialog is open (null = closed). Holds the fase
  // itself, not just its id, so the dialog keeps its title after a refetch.
  const [editFase, setEditFase] = useState<DashboardFase | null>(null);
  const suggestedFaseName = useMemo(() => {
    let max = 0;
    for (const f of fases) {
      const m = /(\d+)/.exec(f.name);
      if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    return `Fase ${Math.max(max, fases.length) + 1}`;
  }, [fases]);

  // Create a fase, then refetch so the new fase (with its auto-created planning
  // child) is authoritative, and select it. The new fase is undated, so
  // nearestFutureFase would not pick it — select it by id explicitly. Create
  // errors propagate to the modal; a refetch failure degrades to an optimistic
  // append so the tab still appears.
  const handleCreateFase = useCallback(
    async (name: string, interventie?: boolean) => {
      const pid = detail.projectId;
      const created = await createProjectFase(pid, name);
      // Tag before the refresh below. The fase exists already, so a failed tag must not
      // bounce the dialog (a retry would create a second fase) — say so, and move on.
      if (interventie) await setFaseInterventie(pid, created.id, true).catch(() => window.alert('Fase aangemaakt, maar de interventie-markering is niet opgeslagen. Zet ze via het potlood opnieuw.'));
      // The new fase's auto-created first task lives in detail.projectTasks — which
      // the "nog in te plannen" backlog reads. createProjectFase guarantees that
      // child exists on return, so refresh the project detail too; without this the
      // backlog stays empty until a manual page refresh.
      void refreshProject(pid);
      try {
        const res = await fetchProjectFases(pid);
        if (projectIdRef.current !== pid) return;
        setFases(res.fases);
        setCollectorFases(res.collectorFases);
        setCollectorenGeleverdState(res.collectorenGeleverd);
        setActiveFase(res.fases.some((f) => f.id === created.id) ? created.id : nearestFutureFase(res.fases));
      } catch {
        if (projectIdRef.current === pid) {
          setFases((prev) => (prev.some((f) => f.id === created.id) ? prev : [...prev, created]));
          setActiveFase(created.id);
        }
      }
      setAddFaseOpen(false);
    },
    [detail.projectId],
  );

  // Rename: the fase name is nowhere a persisted key, so a refetch is enough to
  // propagate it to the tab bar and the planning-day labels. Errors propagate to
  // the dialog, which shows the server's Dutch message.
  const handleRenameFase = useCallback(
    async (name: string, interventie?: boolean) => {
      const fase = editFase;
      if (!fase) return;
      const pid = detail.projectId;
      await renameProjectFase(pid, fase.id, name);
      // The rename already landed: if the tag fails, refresh before the error reaches the dialog, so the bar never keeps the old name.
      if (interventie !== undefined) await setFaseInterventie(pid, fase.id, interventie).catch((err: unknown) => { refetchFaseMapping(); void refreshProject(pid); throw err; });
      setEditFase(null);
      refetchFaseMapping();
      // The fase head's children carry the planning rows the backlog renders, so
      // refresh the project detail too rather than only the fase list.
      void refreshProject(pid);
    },
    [detail.projectId, editFase, refetchFaseMapping, refreshProject],
  );

  // Delete already happened server-side when this fires (the dialog owns the
  // confirm + the 409 refusal). Drop the tab, then refetch. Selecting a
  // remaining fase — or none at all, when the last fase was deleted — is left to
  // refetchFaseMapping, which falls back to nearestFutureFase([]) = null; the
  // tab bar then renders its add-only form.
  const handleFaseDeleted = useCallback(() => {
    const deletedId = editFase?.id;
    setEditFase(null);
    if (deletedId != null) {
      setFases((prev) => prev.filter((f) => f.id !== deletedId));
      setActiveFase((prev) => (prev === deletedId ? null : prev));
    }
    const pid = detail.projectId;
    refetchFaseMapping();
    void refreshProject(pid);
  }, [detail.projectId, editFase, refetchFaseMapping, refreshProject]);

  const collectorsByFase = useMemo(
    () => groupCollectorsByFase(collectorsForProject ?? [], collectorFases),
    [collectorsForProject, collectorFases],
  );

  const isAlles = activeFase === 'alles';
  const activeFaseId = typeof activeFase === 'number' ? activeFase : null;
  const activeFaseObj = activeFaseId !== null ? fases.find((f) => f.id === activeFaseId) ?? null : null;
  const activeCollectors = activeFaseId !== null ? collectorsByFase.get(activeFaseId) ?? [] : [];
  const activeAggregate = useMemo(() => faseAggregate(activeCollectors), [activeCollectors]);
  const activeDateRange = activeFaseId !== null ? faseDateRange(activeFaseId, detail.projectTasks) : null;

  // Pickings scoped to the active fase — built with the SAME merge the per-fase
  // Materiaal section body uses (buildFaseGroups → buildActiveFaseGroup), so the
  // collapsed-header summary count matches the body. The Alles view stays
  // project-wide (it reads the whole materiaalPickings array directly).
  const activeFaseMateriaalPickings = useMemo(() => {
    if (!materiaalForProject || activeFaseId === null) return [];
    const allGroups = buildFaseGroups(
      materiaalPickings ?? [],
      materiaalLines ?? [],
      detail.projectTasks,
      materiaalMoves ?? [],
    );
    return buildActiveFaseGroup(allGroups, detail.projectTasks, activeFaseId).pickingGroups.map(
      (pg) => pg.picking,
    );
  }, [materiaalForProject, activeFaseId, materiaalPickings, materiaalLines, materiaalMoves, detail.projectTasks]);

  // Project-total aggregate (all collectors across every fase). Feeds the
  // page-level DashboardStatusLine badge in Alles mode (statusFaseStatus below).
  const allAggregate = useMemo(() => faseAggregate(collectorsForProject ?? []), [collectorsForProject]);

  // The scheduler is the only per-fase planning view; faseTasks drives its initial
  // week, and the active fase's undated tasks feed the "nog in te plannen" tray.
  const faseTasks = useMemo(
    () => planningTasksForFase(detail.projectTasks, activeFaseId),
    [detail.projectTasks, activeFaseId],
  );
  const unplannedTasks = useMemo(
    () => unplannedTasksForFase(detail.projectTasks, activeFaseId),
    [detail.projectTasks, activeFaseId],
  );
  const handleScheduled = useCallback(() => {
    void refreshProject(detail.projectId);
    refetchFaseMapping();
  }, [detail.projectId, refreshProject, refetchFaseMapping]);

  // Per-section collapse state (chrome only — section bodies are unchanged).
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(DEFAULT_OPEN);

  // Handle a one-shot focusSection request (Collectoren-tab redirect): once the
  // dashboard has loaded, expand the requested section and remember to scroll to
  // it. onFocusConsumed clears the parent's intent so normal navigation is not
  // hijacked on subsequent renders.
  const [pendingScroll, setPendingScroll] = useState<string | null>(null);
  useEffect(() => {
    if (!focusSection || fasesLoading) return;
    // Fase-scoped sections only render once a fase is selected, so their #id anchor
    // needs activeFase !== null. Cross-fase sections (offerte/vordering/activiteit)
    // render regardless — focus them even on a project with zero fases.
    if (!FASE_INDEPENDENT_SECTIONS.has(focusSection as SectionKey) && activeFase === null) return;
    setOpenSections((prev) => ({ ...prev, [focusSection as SectionKey]: true }));
    setPendingScroll(focusSection);
    onFocusConsumed?.();
  }, [focusSection, fasesLoading, activeFase, onFocusConsumed]);

  // Scroll the freshly-opened section into view. Keyed on openSections so it runs
  // after the section has re-rendered with the target open. The #id anchor renders
  // regardless of open/collapsed state, so it's normally present already. Clear the
  // intent unconditionally once consumed — never leave it live (a stale intent would
  // re-fire this effect on later unrelated openSections toggles → surprise scroll).
  useEffect(() => {
    if (!pendingScroll) return;
    document.getElementById(pendingScroll)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPendingScroll(null);
  }, [pendingScroll, openSections]);

  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  // fully_invoiced lives in the vordering payload (fetched inside the section);
  // it reports up here for the status line so we never duplicate the fetch.
  const [fullyInvoiced, setFullyInvoiced] = useState(false);
  // Headline figures the Vordering + Activiteit sections report up for their
  // collapsed headers (null until each section's own fetch resolves).
  const [vorderingData, setVorderingData] = useState<VorderingSummaryData | null>(null);
  const [activiteitCount, setActiviteitCount] = useState<number | null>(null);
  useEffect(() => {
    setFullyInvoiced(false); // reset on project switch until the section re-reports
    setVorderingData(null);
    setActiviteitCount(null);
  }, [detail.projectId]);

  // Status-line signals — all derived in-memory from already-loaded collectors.
  // "foto's wachten" is project-wide (matches the project-level activity/vordering).
  const fotosWachten = useMemo(() => countFotosWachten(collectorsForProject ?? []), [collectorsForProject]);
  const statusFaseStatus = isAlles ? faseStatus(allAggregate) : activeFaseObj ? faseStatus(activeAggregate) : null;

  return (
    <div className="space-y-5">
      {/* Customer warning (`res.partner.sale_warn_msg`) — at the very top so it is
          visible without expanding the collapsed Offerte section, which therefore
          drops its own copy (see DashboardOfferteSection). */}
      {detail.partnerWarning && <PartnerWarningBanner message={detail.partnerWarning} />}

      <DashboardHeaderCard
        detail={detail}
        collectors={collectorsForProject}
        collectorsLoading={collectorsLoading}
        collectorsError={collectorsError}
      />

      {/* Status / "needs attention" line — all derived in-memory. */}
      <DashboardStatusLine
        faseStatus={statusFaseStatus}
        fullyInvoiced={fullyInvoiced}
        fotosWachten={fotosWachten}
      />

      {/* Project-wide free-text notes (Fase 4) — not fase-scoped. Read-only for
          roles without project:notes:write (the autosave PATCH would 403). */}
      <DashboardNotesSection
        projectId={detail.projectId}
        initialNotes={typeof detail.project?.x_dashboard_notes === 'string' ? detail.project.x_dashboard_notes : ''}
        readOnly={!canNotesWrite}
      />

      {/* Werf-level "collectoren geleverd" status box — placed under Notities
          (moved out of the Collectoren section per Igor's request). */}
      <DashboardCollectorenGeleverdBox
        collectorenGeleverd={collectorenGeleverd}
        onToggle={handleToggleCollectorenGeleverd}
        saving={collectorenGeleverdSaving}
      />

      {/* Fase tabs + "+ Fase toevoegen". The bar is rendered once fases have
          loaded, even for 0–1-fase projects, so the add button is always
          reachable. The "Alles" roll-up tab is still gated to ≥2 fases inside
          FaseTabBar. */}
      {!fasesLoading && (
        <FaseTabBar
          fases={fases}
          activeFaseId={activeFase}
          onSelect={(id) => setActiveFase(id)}
          // "+ Fase toevoegen" writes (POST /planning/…/fases = planning:write) —
          // omit the button for read-only roles so the bar shows tabs only.
          onAddFase={canPlanningWrite ? () => setAddFaseOpen(true) : undefined}
          // Rename/delete are the same planning:write boundary as add — a role
          // without it (oplijster has the dashboard tab but not the permission)
          // gets no affordance at all.
          onEditFase={canPlanningWrite ? (fase) => setEditFase(fase) : undefined}
          projectTasks={detail.projectTasks}
        />
      )}

      {/* ── Installateur Taak ── under the fase selector, open; shows the day that fits the fase. */}
      {canInstallateurTaak && (
        <DashboardSection
          id="installateur_taak"
          color="navy"
          icon="🔧"
          title="Installateur Taak"
          summary="Overzicht voor installateurs"
          open={openSections.installateur_taak}
          onToggle={() => toggleSection('installateur_taak')}
        >
          <InstallateurTaakContent projectId={detail.projectId} projectName={detail.projectName} detail={detail} faseId={activeFase} />
        </DashboardSection>
      )}

      {/* Section order (matches the pill nav): planning → offerte → materiaal →
          collectoren → vordering → recente activiteit. Planning / materiaal /
          collectoren are fase-scoped (rendered per active fase or the "Alles"
          roll-up); offerte / vordering / activiteit are cross-fase (rendered
          once). To keep offerte sitting between planning and materiaal, the
          fase-scoped blocks are split into per-section conditionals rather than
          one fragment each. */}

      {/* ── Planning ─────────────────────────────────────────────────────── */}
      {/* "Alles" has no scheduler and nothing planning-specific to roll up, so it
          renders NO Planning section at all — not even an empty shell. Everything
          the old "Project-totaal" strip carried now lives where it is read: the
          counts in the Collectoren header, the ratio in DashboardStatusLine, and
          the systeem/m² mix under the projectkop's "Uitgevoerd m²" tile. */}
      {!isAlles && activeFaseObj && (
        <DashboardSection
          id="planning"
          color="blue"
          icon="📅"
          title="Planning"
          summary={planningSummary(activeFaseObj, activeAggregate, activeDateRange)}
          open={openSections.planning}
          onToggle={() => toggleSection('planning')}
        >
          <FaseStatusStrip fase={activeFaseObj} aggregate={activeAggregate} dateRange={activeDateRange} embedded />
          {/* The scheduler IS the per-fase planning view. Write roles (planning:write)
              get the interactive board; read-only roles get the SAME board in read-only
              mode — cards don't drag (PlanningGrid self-gates on the planning_edit
              feature) and the write-only tray/recover surface is suppressed. */}
          <div className="mt-4">
            <InplannenPanel
              key={activeFaseId ?? 'none'}
              readOnly={!canPlanningWrite}
              projectId={detail.projectId}
              projectName={detail.projectName}
              faseId={activeFaseId}
              faseName={activeFaseObj.name}
              unplannedTasks={unplannedTasks}
              projectTasks={detail.projectTasks}
              installerNames={detail.installerNames}
              initialWeekDateKey={dateKey(faseStartWeek(faseTasks))}
              onScheduled={handleScheduled}
            />
          </div>
        </DashboardSection>
      )}

      {/* ── Offerte ──────── cross-fase, project-level ─────────────────────── */}
      {/* Sale orders / verkooporders. Data ships inside `detail` (project-detail
          payload), so no mount-time fetch here; the gate mirrors the standalone
          Offerte tab it replaces on the dashboard. */}
      {canOfferteRead && (
        <DashboardSection
          id="offerte"
          color="blue"
          icon="🧾"
          title="Offerte"
          summary={(() => {
            const n = (detail.saleOrders?.length ?? 0) || (detail.saleOrder ? 1 : 0);
            return n === 0 ? 'Geen verkooporder' : n === 1 ? '1 verkooporder' : `${n} verkooporders`;
          })()}
          open={openSections.offerte}
          onToggle={() => toggleSection('offerte')}
        >
          <DashboardOfferteSection detail={detail} embedded />
        </DashboardSection>
      )}

      {/* ── Materiaal ─────────────────────────────────────────────────────── */}
      {isAlles && (
        <DashboardSection
          id="materiaal"
          color="amber"
          icon="📦"
          title="Materiaal"
          summary={materiaalForProject ? materiaalSummary(materiaalPickings ?? []) : undefined}
          open={openSections.materiaal}
          onToggle={() => toggleSection('materiaal')}
        >
          <DashboardMateriaalAlles
            projectId={detail.projectId}
            projectTasks={detail.projectTasks}
            fases={fases}
            loading={!materiaalForProject && materiaalLoading}
            error={materiaalForProject ? materiaalError : null}
            embedded
          />
        </DashboardSection>
      )}
      {/* Fase 6b — materiaal (view + upload + CAD + delete + opgelijst), scoped to the active fase. */}
      {!isAlles && activeFaseObj && (
        <DashboardSection
          id="materiaal"
          color="amber"
          icon="📦"
          title="Materiaal"
          summary={materiaalForProject ? materiaalSummary(activeFaseMateriaalPickings) : undefined}
          open={openSections.materiaal}
          onToggle={() => toggleSection('materiaal')}
        >
          <DashboardMateriaalSection
            projectId={detail.projectId}
            projectTasks={detail.projectTasks}
            activeFaseId={activeFaseObj.id}
            loading={!materiaalForProject && materiaalLoading}
            error={materiaalForProject ? materiaalError : null}
            embedded
          />
        </DashboardSection>
      )}

      {/* ── Collectoren ───────────────────────────────────────────────────── */}
      {isAlles && (
        <DashboardSection
          id="collectoren"
          color="purple"
          icon="🔘"
          title="Collectoren"
          summary={collectorenSummary(collectorsForProject ?? [])}
          open={openSections.collectoren}
          onToggle={() => toggleSection('collectoren')}
        >
          <DashboardCollectorenSection
            projectId={detail.projectId}
            projectName={detail.projectName}
            collectors={collectorsForProject ?? []}
            installerNames={detail.installerNames}
            loading={collectorsForProject === null && !collectorsError}
            error={collectorsError}
            onChanged={handleCollectorChanged}
            faseLabel="dit project"
            collectorenGeleverd={collectorenGeleverd}
            embedded
          />
        </DashboardSection>
      )}
      {/* Fase 5 — collectoren, scoped to the active fase (clean list + full detail on expand). */}
      {!isAlles && activeFaseObj && (
        <DashboardSection
          id="collectoren"
          color="purple"
          icon="🔘"
          title="Collectoren"
          summary={collectorenSummary(activeCollectors)}
          open={openSections.collectoren}
          onToggle={() => toggleSection('collectoren')}
        >
          <DashboardCollectorenSection
            projectId={detail.projectId}
            projectName={detail.projectName}
            collectors={activeCollectors}
            installerNames={detail.installerNames}
            loading={collectorsForProject === null && !collectorsError}
            error={collectorsError}
            onChanged={handleCollectorChanged}
            collectorenGeleverd={collectorenGeleverd}
            embedded
          />
        </DashboardSection>
      )}

      {/* ── Interventies ──────── one block per Interventie-tagged fase + "Oudere" ─ */}
      {/* Gated on photos:read so it never mounts (and 403s its GET) for a role without
          photo access. A mutation bumps interventieRev → the Vordering section reloads
          its selectable interventie date-groups. */}
      {canPhotosRead && (
        <DashboardSection
          id="interventie"
          color="purple"
          icon="🛠️"
          title="Interventies"
          open={openSections.interventie}
          onToggle={() => toggleSection('interventie')}
        >
          <DashboardInterventieBlocks
            projectId={detail.projectId}
            fases={fases}
            projectTasks={detail.projectTasks}
            planningDates={interventiePlanningDates}
            onChanged={handleInterventieChanged}
          />
        </DashboardSection>
      )}

      {fasesLoading && <p className="px-1 text-sm text-td-muted">Fases laden…</p>}
      {fasesError && <p className="px-1 text-sm text-td-magenta">Fases laden mislukt: {fasesError}</p>}

      {/* Fase 9b — vordering (client billing). Project-level, cross-fase: shows
          every collector grouped by fase, ignores the active fase. Rendered once
          (not per-fase) so the overview is always reachable. Gated on vordering:read
          — CONDITIONALLY RENDERED (not just collapsed) so the section's mount-time
          GET /projects/:id/vorderingen never fires for roles that would 403. */}
      {canVorderingRead && (
        <DashboardSection
          id="vordering"
          color="green"
          icon="💶"
          title="Vordering"
          summary={vorderingSummary(vorderingData)}
          open={openSections.vordering}
          onToggle={() => toggleSection('vordering')}
        >
          <DashboardVorderingSection
            projectId={detail.projectId}
            fases={fases}
            collectorFases={collectorFases}
            refreshSignal={collectorRev}
            interventieRefreshSignal={interventieRev}
            onChanged={refetchFaseMapping}
            onFullyInvoicedChange={setFullyInvoiced}
            onSummaryChange={setVorderingData}
            embedded
          />
        </DashboardSection>
      )}

      {/* Project-wide recent activity (Fase 7) — scoped polling, last 5 entries.
          Gated on project:activity:read (conditional render → no mount fetch for
          roles that would 403). The Odoo-chatter primary feed is separately gated
          on project:chatter:read (admin-only/GDPR) via chatterEnabled — for a role
          with activity but not chatter (projectleider) the activity-log becomes the
          primary feed instead of 403-ing on chatter. */}
      {canActivityRead && (
        <DashboardSection
          id="activiteit"
          color="navy"
          icon="🕘"
          title="Recente activiteit"
          summary={activiteitSummary(activiteitCount)}
          open={openSections.activiteit}
          onToggle={() => toggleSection('activiteit')}
        >
          <DashboardActivityWidget
            projectId={detail.projectId}
            onCountChange={setActiviteitCount}
            chatterEnabled={canChatterRead}
            embedded
          />
        </DashboardSection>
      )}

      <p className="px-1 text-sm text-td-muted">
        Meer dashboard-secties (onderaannemers) volgen.
      </p>

      {addFaseOpen && (
        <FaseModal
          mode="create"
          initialName={suggestedFaseName}
          onSubmit={handleCreateFase}
          onClose={() => setAddFaseOpen(false)}
        />
      )}

      {editFase && (
        <FaseModal
          mode="edit"
          projectId={detail.projectId}
          faseId={editFase.id}
          initialName={editFase.name}
          initialInterventie={detail.projectTasks.some((t) => t.id === editFase.id && t.isInterventie)}
          onSubmit={handleRenameFase}
          onDeleted={handleFaseDeleted}
          onClose={() => setEditFase(null)}
        />
      )}
    </div>
  );
}
